import { useState } from "react";

export interface PairPoint { key: string; label: string; value: number | null; previous: number; valueLabel: string; previousLabel: string }

/** Two columns per period: this year beside last year. Two series, so a
 *  legend is always shown (and the tooltip names both); categorical slots 1
 *  and 2, validated together in both themes. A 2px gap between the pair and
 *  between periods; one hover target per period, the full plot height; a
 *  table view for anyone who cannot use the chart. A period not reported yet
 *  shows last year alone. */
export function PairBars({ points, current, previous, title }: {
  points: PairPoint[]; current: string; previous: string; title: string;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const [asTable, setAsTable] = useState(false);
  const max = Math.max(...points.map((p) => Math.max(p.value ?? 0, p.previous)), 1);
  const h = (v: number | null) => `${v ? Math.max((v / max) * 100, 1) : 0}%`;

  return (
    <div className="monthbars">
      <div className="chart-tools">
        <span className="legend" aria-hidden={asTable}>
          <span className="legend-key"><span className="swatch s1" />{current}</span>
          <span className="legend-key"><span className="swatch s2" />{previous}</span>
        </span>
        <button className="link-btn" onClick={() => setAsTable((t) => !t)} aria-pressed={asTable}>
          {asTable ? "Show chart" : "Show as table"}
        </button>
      </div>
      {asTable ? (
        <table className="grid compact">
          <thead><tr><th>{title}</th><th className="n">{current}</th><th className="n">{previous}</th></tr></thead>
          <tbody>{points.map((p) => (
            <tr key={p.key}><td>{p.label}</td><td className="n">{p.valueLabel}</td><td className="n">{p.previousLabel}</td></tr>
          ))}</tbody>
        </table>
      ) : (
        <div className="mb-plot" role="img" aria-label={`${title}: ${current} against ${previous}`}>
          <div className="mb-cols">
            {points.map((p, i) => (
              <button key={p.key} className="mb-col pair" onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}
                      onFocus={() => setHover(i)} onBlur={() => setHover(null)}
                      aria-label={`${p.label}: ${current} ${p.valueLabel}, ${previous} ${p.previousLabel}`}>
                <span className="mb-fill" style={{ blockSize: h(p.value) }} />
                <span className="mb-fill prev" style={{ blockSize: h(p.previous) }} />
                {hover === i && (
                  <span className="tooltip" role="tooltip">
                    <strong>{p.label}</strong><br />
                    <span className="swatch s1" /> {current}: {p.valueLabel}<br />
                    <span className="swatch s2" /> {previous}: {p.previousLabel}
                  </span>
                )}
              </button>
            ))}
          </div>
          <div className="mb-axis">{points.map((p) => <span key={p.key}>{p.label}</span>)}</div>
        </div>
      )}
    </div>
  );
}
